import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Bot, Globe, Settings, Play, Loader2, CheckCircle, XCircle,
    AlertCircle, RefreshCw, Zap, Brain, Calendar, MapPin, ChevronDown,
    Eye, User, Star, TrendingUp, FileText, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available regions for AI picks
const AVAILABLE_REGIONS = [
    { id: 'uk', name: 'United Kingdom', flag: '🇬🇧' },
    { id: 'ire', name: 'Ireland', flag: '🇮🇪' },
    { id: 'usa', name: 'United States', flag: '🇺🇸' },
    { id: 'aus', name: 'Australia', flag: '🇦🇺' },
    { id: 'fra', name: 'France', flag: '🇫🇷' },
    { id: 'uae', name: 'UAE', flag: '🇦🇪' },
    { id: 'hk', name: 'Hong Kong', flag: '🇭🇰' },
    { id: 'jpn', name: 'Japan', flag: '🇯🇵' },
];

export default function AdminAISettingsPage() {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    
    const [settings, setSettings] = useState({
        enabled: false,
        enabled_regions: []
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // AI Generation State
    const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [meetings, setMeetings] = useState([]);
    const [loadingMeetings, setLoadingMeetings] = useState(false);
    const [selectedMeetings, setSelectedMeetings] = useState([]);
    const [selectedRace, setSelectedRace] = useState('');
    const [races, setRaces] = useState([]);
    const [loadingRaces, setLoadingRaces] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, meeting: '' });
    const [generationResults, setGenerationResults] = useState([]);
    
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        if (!user?.is_admin) {
            navigate('/');
            toast.error('Admin access required');
            return;
        }
        fetchSettings();
    }, [isAuthenticated, user, navigate]);
    
    // Fetch meetings when date changes
    useEffect(() => {
        if (selectedDate) {
            fetchMeetings(selectedDate);
        }
    }, [selectedDate]);
    
    const fetchSettings = async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/admin/ai-settings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings(res.data);
        } catch (err) {
            console.error('Failed to fetch AI settings:', err);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };
    
    const fetchMeetings = async (date) => {
        setLoadingMeetings(true);
        setMeetings([]);
        setSelectedMeetings([]);
        setRaces([]);
        setSelectedRace('');
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/racing/meetings?date=${date}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const meetingsList = res.data.meetings || res.data || [];
            setMeetings(meetingsList);
        } catch (err) {
            console.error('Failed to fetch meetings:', err);
            toast.error('Failed to load meetings');
        } finally {
            setLoadingMeetings(false);
        }
    };
    
    const fetchRacesForMeeting = async (meetingId) => {
        if (!meetingId) {
            setRaces([]);
            return;
        }
        setLoadingRaces(true);
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/racing/meetings/${meetingId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const racesList = res.data.events || res.data.races || [];
            setRaces(racesList);
        } catch (err) {
            console.error('Failed to fetch races:', err);
            toast.error('Failed to load races');
        } finally {
            setLoadingRaces(false);
        }
    };
    
    const saveSettings = async () => {
        setSaving(true);
        try {
            const token = getToken();
            await axios.put(`${API_URL}/api/admin/ai-settings`, settings, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('AI settings saved');
        } catch (err) {
            console.error('Failed to save settings:', err);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };
    
    const toggleRegion = (regionId) => {
        setSettings(prev => {
            const regions = prev.enabled_regions || [];
            if (regions.includes(regionId)) {
                return {
                    ...prev,
                    enabled_regions: regions.filter(r => r !== regionId)
                };
            } else {
                return {
                    ...prev,
                    enabled_regions: [...regions, regionId]
                };
            }
        });
    };
    
    const toggleMeetingSelection = (meetingId) => {
        setSelectedMeetings(prev => {
            if (prev.includes(meetingId)) {
                return prev.filter(id => id !== meetingId);
            } else {
                return [...prev, meetingId];
            }
        });
        // If only one meeting selected, fetch its races
        if (!selectedMeetings.includes(meetingId)) {
            fetchRacesForMeeting(meetingId);
        } else if (selectedMeetings.length === 1) {
            setRaces([]);
        }
    };
    
    const selectAllMeetings = () => {
        if (selectedMeetings.length === meetings.length) {
            setSelectedMeetings([]);
            setRaces([]);
        } else {
            setSelectedMeetings(meetings.map(m => m.id));
        }
    };
    
    const generateForSingleRace = async () => {
        if (!selectedRace) {
            toast.error('Please select a race');
            return;
        }
        
        setGenerating(true);
        setGenerationResults([]);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/ai-picks/generate/${selectedRace}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setGenerationResults([{
                type: 'race',
                success: true,
                message: res.data.message,
                picks: res.data.picks
            }]);
            toast.success('AI picks generated!');
        } catch (err) {
            setGenerationResults([{
                type: 'race',
                success: false,
                message: err.response?.data?.detail || 'Failed to generate picks'
            }]);
            toast.error('Failed to generate picks');
        } finally {
            setGenerating(false);
        }
    };
    
    const generateForSelectedMeetings = async () => {
        if (selectedMeetings.length === 0) {
            toast.error('Please select at least one meeting');
            return;
        }
        
        setGenerating(true);
        setGenerationResults([]);
        setGenerationProgress({ current: 0, total: selectedMeetings.length, meeting: '' });
        
        const results = [];
        const token = getToken();
        
        for (let i = 0; i < selectedMeetings.length; i++) {
            const meetingId = selectedMeetings[i];
            const meeting = meetings.find(m => m.id === meetingId);
            setGenerationProgress({ 
                current: i + 1, 
                total: selectedMeetings.length, 
                meeting: meeting?.name || meetingId 
            });
            
            try {
                const res = await axios.post(
                    `${API_URL}/api/admin/ai-picks/generate-meeting/${meetingId}`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                results.push({
                    type: 'meeting',
                    meetingName: meeting?.name || meetingId,
                    success: true,
                    message: res.data.message,
                    generated: res.data.generated,
                    errors: res.data.errors
                });
            } catch (err) {
                results.push({
                    type: 'meeting',
                    meetingName: meeting?.name || meetingId,
                    success: false,
                    message: err.response?.data?.detail || 'Failed'
                });
            }
        }
        
        setGenerationResults(results);
        setGenerating(false);
        
        const successCount = results.filter(r => r.success).length;
        const totalGenerated = results.reduce((sum, r) => sum + (r.generated || 0), 0);
        toast.success(`Generated AI picks for ${totalGenerated} races across ${successCount} meetings`);
    };
    
    // Generate date options (today + next 7 days)
    const dateOptions = [];
    for (let i = 0; i < 7; i++) {
        const date = addDays(new Date(), i);
        dateOptions.push({
            value: format(date, 'yyyy-MM-dd'),
            label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : format(date, 'EEE, MMM d')
        });
    }
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background p-4">
                <div className="max-w-4xl mx-auto space-y-6">
                    <Skeleton className="h-12 w-64" />
                    <Skeleton className="h-96" />
                </div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-background p-4">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Bot className="h-8 w-8 text-primary" />
                    <div>
                        <h1 className="text-3xl font-bold page-title">AI Picks Settings</h1>
                        <p className="text-muted-foreground">Configure AI-generated race predictions</p>
                    </div>
                </div>
                
                {/* Main Settings Card */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Settings className="h-5 w-5" />
                            General Settings
                        </CardTitle>
                        <CardDescription>Enable or disable AI picks and configure regions</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Master Enable */}
                        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                            <div className="flex items-center gap-3">
                                <Zap className="h-6 w-6 text-amber-500" />
                                <div>
                                    <Label className="text-base font-semibold">Enable AI Picks</Label>
                                    <p className="text-sm text-muted-foreground">Turn on AI-generated predictions</p>
                                </div>
                            </div>
                            <Switch
                                checked={settings.enabled}
                                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                            />
                        </div>
                        
                        <Separator />
                        
                        {/* Region Selection */}
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <Globe className="h-5 w-5 text-blue-500" />
                                <Label className="text-base font-semibold">Enabled Regions</Label>
                            </div>
                            <p className="text-sm text-muted-foreground mb-4">
                                Select which racing regions should have AI picks generated
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {AVAILABLE_REGIONS.map(region => (
                                    <div
                                        key={region.id}
                                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                            settings.enabled_regions?.includes(region.id)
                                                ? 'border-primary bg-primary/10'
                                                : 'border-muted hover:border-muted-foreground/50'
                                        }`}
                                        onClick={() => toggleRegion(region.id)}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                checked={settings.enabled_regions?.includes(region.id)}
                                                onCheckedChange={() => toggleRegion(region.id)}
                                            />
                                            <span className="text-xl">{region.flag}</span>
                                            <span className="text-sm font-medium">{region.name}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter>
                        <Button onClick={saveSettings} disabled={saving}>
                            {saving ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Save Settings
                                </>
                            )}
                        </Button>
                    </CardFooter>
                </Card>
                
                {/* Generate AI Picks Card - Improved */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Brain className="h-5 w-5" />
                            Generate AI Picks
                        </CardTitle>
                        <CardDescription>Select date, meetings, and races to generate AI predictions</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Step 1: Date Selection */}
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</div>
                                <Label className="font-semibold">Select Date</Label>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {dateOptions.map(opt => (
                                    <Button
                                        key={opt.value}
                                        variant={selectedDate === opt.value ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setSelectedDate(opt.value)}
                                    >
                                        <Calendar className="h-4 w-4 mr-1" />
                                        {opt.label}
                                    </Button>
                                ))}
                            </div>
                        </div>
                        
                        <Separator />
                        
                        {/* Step 2: Meeting Selection */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">2</div>
                                    <Label className="font-semibold">Select Meetings</Label>
                                </div>
                                {meetings.length > 0 && (
                                    <Button variant="ghost" size="sm" onClick={selectAllMeetings}>
                                        {selectedMeetings.length === meetings.length ? 'Deselect All' : 'Select All'}
                                    </Button>
                                )}
                            </div>
                            
                            {loadingMeetings ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-16" />)}
                                </div>
                            ) : meetings.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                    <p>No meetings found for this date</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {meetings.map(meeting => (
                                        <div
                                            key={meeting.id}
                                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                                selectedMeetings.includes(meeting.id)
                                                    ? 'border-primary bg-primary/10'
                                                    : 'border-muted hover:border-muted-foreground/50'
                                            }`}
                                            onClick={() => toggleMeetingSelection(meeting.id)}
                                        >
                                            <div className="flex items-start gap-2">
                                                <Checkbox
                                                    checked={selectedMeetings.includes(meeting.id)}
                                                    onCheckedChange={() => toggleMeetingSelection(meeting.id)}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm truncate">{meeting.name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {meeting.events?.length || meeting.raceCount || '?'} races
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {selectedMeetings.length > 0 && (
                                <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg">
                                    <span className="text-sm">
                                        <strong>{selectedMeetings.length}</strong> meeting{selectedMeetings.length > 1 ? 's' : ''} selected
                                    </span>
                                    <Button 
                                        onClick={generateForSelectedMeetings}
                                        disabled={generating}
                                        className="gap-2"
                                    >
                                        {generating ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Play className="h-4 w-4" />
                                        )}
                                        Generate for All Selected
                                    </Button>
                                </div>
                            )}
                        </div>
                        
                        <Separator />
                        
                        {/* Step 3: Optional - Single Race */}
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-bold">3</div>
                                <Label className="font-semibold">Or Select Single Race (Optional)</Label>
                            </div>
                            
                            {selectedMeetings.length === 1 ? (
                                <div className="flex gap-2">
                                    <Select
                                        value={selectedRace}
                                        onValueChange={setSelectedRace}
                                        disabled={loadingRaces}
                                    >
                                        <SelectTrigger className="flex-1">
                                            <SelectValue placeholder={loadingRaces ? "Loading races..." : "Select a race"} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {races.map(race => (
                                                <SelectItem key={race.id} value={race.id}>
                                                    {race.startTimeUtc ? format(new Date(race.startTimeUtc), 'HH:mm') : ''} - {race.title || race.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button 
                                        onClick={generateForSingleRace}
                                        disabled={generating || !selectedRace}
                                    >
                                        {generating ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Play className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground italic">
                                    Select exactly one meeting to choose a specific race
                                </p>
                            )}
                        </div>
                        
                        {/* Progress Bar */}
                        {generating && generationProgress.total > 0 && (
                            <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
                                <div className="flex justify-between text-sm">
                                    <span>Processing: {generationProgress.meeting}</span>
                                    <span>{generationProgress.current} / {generationProgress.total}</span>
                                </div>
                                <Progress value={(generationProgress.current / generationProgress.total) * 100} className="h-2" />
                            </div>
                        )}
                        
                        {/* Generation Results */}
                        {generationResults.length > 0 && (
                            <div className="space-y-2">
                                <Label className="font-semibold">Results</Label>
                                {generationResults.map((result, idx) => (
                                    <div
                                        key={idx}
                                        className={`p-3 rounded-lg ${
                                            result.success 
                                                ? 'bg-green-500/10 border border-green-500/30' 
                                                : 'bg-red-500/10 border border-red-500/30'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            {result.success ? (
                                                <CheckCircle className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <XCircle className="h-4 w-4 text-red-500" />
                                            )}
                                            <span className="font-medium">
                                                {result.meetingName || 'Race'}
                                            </span>
                                            <span className="text-sm text-muted-foreground">
                                                {result.message}
                                            </span>
                                        </div>
                                        
                                        {result.picks && (
                                            <div className="mt-2 pl-6 space-y-1">
                                                {Object.entries(result.picks).map(([type, pick]) => {
                                                    if (type === 'overall_analysis' || type === 'error' || !pick?.name) return null;
                                                    return (
                                                        <div key={type} className="flex items-center gap-2 text-sm">
                                                            <Badge variant="outline" className="capitalize text-xs">{type.replace('_', ' ')}</Badge>
                                                            <span>{pick.name}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        
                                        {result.errors && result.errors.length > 0 && (
                                            <div className="mt-2 pl-6">
                                                <p className="text-xs text-red-400">{result.errors.length} errors</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
                
                {/* Info Card */}
                <Card className="border-blue-500/30 bg-blue-500/5">
                    <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-blue-500">How AI Picks Work</h4>
                                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                                    <li>• AI analyzes form, ratings, odds, and jockey/trainer stats</li>
                                    <li>• It also considers Podium picks for context</li>
                                    <li>• Picks are stored and displayed alongside Podium tips on race pages</li>
                                    <li>• Performance is tracked on the "Podium vs AI" page</li>
                                </ul>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Refresh All AI Picks Card */}
                <RefreshAIPicksCard />
                
                {/* AI Data Preview Card */}
                <AIDataPreviewCard meetings={meetings} selectedDate={selectedDate} />
            </div>
        </div>
    );
}

// Separate component for Refresh AI Picks functionality
function RefreshAIPicksCard() {
    const [refreshing, setRefreshing] = useState(false);
    const [result, setResult] = useState(null);
    
    const refreshAllPicks = async () => {
        if (!window.confirm('This will regenerate ALL existing AI picks with fresh data. This may take several minutes. Continue?')) {
            return;
        }
        
        setRefreshing(true);
        setResult(null);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/ai-picks/refresh-all`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setResult({
                success: true,
                message: res.data.message,
                refreshed: res.data.refreshed,
                total: res.data.total,
                errors: res.data.errors
            });
            toast.success(res.data.message);
        } catch (err) {
            setResult({
                success: false,
                message: err.response?.data?.detail || 'Failed to refresh AI picks'
            });
            toast.error('Failed to refresh AI picks');
        } finally {
            setRefreshing(false);
        }
    };
    
    return (
        <Card className="border-amber-500/30 bg-amber-500/5">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 text-amber-500" />
                    Refresh All AI Picks
                </CardTitle>
                <CardDescription>
                    Regenerate all existing AI picks with updated data (Podium tips, race times, etc.)
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="p-3 bg-amber-500/10 rounded-lg">
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                        <strong>When to use:</strong> If AI picks were generated before recent updates, 
                        refreshing will ensure they include Podium tips for the Podium vs AI comparison page.
                    </p>
                </div>
                
                <Button 
                    onClick={refreshAllPicks}
                    disabled={refreshing}
                    variant="outline"
                    className="border-amber-500 text-amber-600 hover:bg-amber-500/10"
                >
                    {refreshing ? (
                        <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Refreshing All Picks...
                        </>
                    ) : (
                        <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh All AI Picks
                        </>
                    )}
                </Button>
                
                {result && (
                    <div className={`p-3 rounded-lg ${
                        result.success 
                            ? 'bg-green-500/10 border border-green-500/30' 
                            : 'bg-red-500/10 border border-red-500/30'
                    }`}>
                        <div className="flex items-center gap-2">
                            {result.success ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                                <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            <span className="font-medium">{result.message}</span>
                        </div>
                        {result.errors && result.errors.length > 0 && (
                            <div className="mt-2 text-sm text-muted-foreground">
                                <p className="text-red-400">{result.errors.length} errors occurred</p>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// AI Data Preview Card Component
function AIDataPreviewCard({ meetings, selectedDate }) {
    const [selectedMeeting, setSelectedMeeting] = useState('');
    const [races, setRaces] = useState([]);
    const [selectedRaceId, setSelectedRaceId] = useState('');
    const [loadingRaces, setLoadingRaces] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [expandedEntrants, setExpandedEntrants] = useState({});
    
    const fetchRacesForMeeting = async (meetingId) => {
        if (!meetingId) {
            setRaces([]);
            return;
        }
        setLoadingRaces(true);
        setSelectedRaceId('');
        setPreviewData(null);
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/racing/meetings/${meetingId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const racesList = res.data.events || res.data.races || [];
            setRaces(racesList);
        } catch (err) {
            console.error('Failed to fetch races:', err);
            toast.error('Failed to load races');
        } finally {
            setLoadingRaces(false);
        }
    };
    
    const fetchPreviewData = async () => {
        if (!selectedRaceId) {
            toast.error('Please select a race');
            return;
        }
        setLoadingPreview(true);
        setPreviewData(null);
        try {
            const token = getToken();
            const res = await axios.get(
                `${API_URL}/api/admin/ai-picks/preview/${selectedRaceId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPreviewData(res.data);
        } catch (err) {
            console.error('Failed to fetch preview:', err);
            toast.error(err.response?.data?.detail || 'Failed to load AI data preview');
        } finally {
            setLoadingPreview(false);
        }
    };
    
    const toggleEntrant = (name) => {
        setExpandedEntrants(prev => ({
            ...prev,
            [name]: !prev[name]
        }));
    };
    
    return (
        <Card className="border-cyan-500/30 bg-cyan-500/5">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5 text-cyan-500" />
                    AI Data Preview
                </CardTitle>
                <CardDescription>
                    See exactly what data the AI receives when generating picks - perfect for demos and debugging
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Meeting Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label>Select Meeting</Label>
                        <Select 
                            value={selectedMeeting} 
                            onValueChange={(v) => {
                                setSelectedMeeting(v);
                                fetchRacesForMeeting(v);
                            }}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Choose a meeting..." />
                            </SelectTrigger>
                            <SelectContent>
                                {meetings.map(meeting => (
                                    <SelectItem key={meeting.id} value={meeting.id}>
                                        <span className="flex items-center gap-2">
                                            <MapPin className="h-3 w-3" />
                                            {meeting.name}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    
                    <div className="space-y-2">
                        <Label>Select Race</Label>
                        <Select 
                            value={selectedRaceId} 
                            onValueChange={setSelectedRaceId}
                            disabled={!selectedMeeting || loadingRaces}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder={loadingRaces ? "Loading races..." : "Choose a race..."} />
                            </SelectTrigger>
                            <SelectContent>
                                {races.map(race => (
                                    <SelectItem key={race.id} value={race.id}>
                                        <span className="flex items-center gap-2">
                                            <Calendar className="h-3 w-3" />
                                            {race.start ? format(new Date(race.start), 'HH:mm') : ''} - {race.title?.substring(0, 40)}...
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                
                <Button 
                    onClick={fetchPreviewData}
                    disabled={!selectedRaceId || loadingPreview}
                    className="bg-cyan-600 hover:bg-cyan-700"
                >
                    {loadingPreview ? (
                        <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading Preview...
                        </>
                    ) : (
                        <>
                            <Eye className="h-4 w-4 mr-2" />
                            Preview AI Data
                        </>
                    )}
                </Button>
                
                {/* Preview Results */}
                {previewData && (
                    <div className="space-y-4 mt-4">
                        <Separator />
                        
                        {/* Race Info */}
                        <div className="space-y-3">
                            <h3 className="font-semibold flex items-center gap-2">
                                <FileText className="h-4 w-4 text-cyan-500" />
                                Race Information
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                <div className="p-2 bg-background rounded border">
                                    <p className="text-muted-foreground text-xs">Race</p>
                                    <p className="font-medium truncate" title={previewData.race_info?.race_title}>
                                        {previewData.race_info?.race_title}
                                    </p>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                    <p className="text-muted-foreground text-xs">Distance</p>
                                    <p className="font-medium">{previewData.race_info?.distance || 'N/A'}</p>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                    <p className="text-muted-foreground text-xs">Going</p>
                                    <p className="font-medium">{previewData.race_info?.going || 'N/A'}</p>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                    <p className="text-muted-foreground text-xs">Class</p>
                                    <p className="font-medium">{previewData.race_info?.class || 'N/A'}</p>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                    <p className="text-muted-foreground text-xs">Race Type</p>
                                    <p className="font-medium">{previewData.race_info?.race_type || 'N/A'}</p>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                    <p className="text-muted-foreground text-xs">Surface</p>
                                    <p className="font-medium">{previewData.race_info?.surface || 'N/A'}</p>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                    <p className="text-muted-foreground text-xs">Eligibility</p>
                                    <p className="font-medium">{previewData.race_info?.eligibility || 'N/A'}</p>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                    <p className="text-muted-foreground text-xs">Runners</p>
                                    <p className="font-medium">{previewData.total_entrants || 0}</p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Podium Tips */}
                        {previewData.human_expert_tips && Object.keys(previewData.human_expert_tips).length > 0 && (
                            <div className="space-y-3">
                                <h3 className="font-semibold flex items-center gap-2">
                                    <Users className="h-4 w-4 text-amber-500" />
                                    Podium Tips
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {previewData.human_expert_tips.top_pick && (
                                        <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">
                                            <Star className="h-3 w-3 mr-1" />
                                            Top Pick: {previewData.human_expert_tips.top_pick}
                                        </Badge>
                                    )}
                                    {previewData.human_expert_tips.each_way && (
                                        <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30">
                                            Each-Way: {previewData.human_expert_tips.each_way}
                                        </Badge>
                                    )}
                                    {previewData.human_expert_tips.dark_horse && (
                                        <Badge className="bg-purple-500/20 text-purple-600 border-purple-500/30">
                                            <TrendingUp className="h-3 w-3 mr-1" />
                                            Dark Horse: {previewData.human_expert_tips.dark_horse}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Entrants */}
                        <div className="space-y-3">
                            <h3 className="font-semibold flex items-center gap-2">
                                <User className="h-4 w-4 text-cyan-500" />
                                Entrants Data ({previewData.total_entrants})
                            </h3>
                            <p className="text-xs text-muted-foreground">
                                Click on each horse to see full data sent to AI
                            </p>
                            <ScrollArea className="h-[400px] pr-4">
                                <div className="space-y-2">
                                    {previewData.entrants?.map((entrant, idx) => (
                                        <Collapsible 
                                            key={idx}
                                            open={expandedEntrants[entrant.name]}
                                            onOpenChange={() => toggleEntrant(entrant.name)}
                                        >
                                            <CollapsibleTrigger asChild>
                                                <div className="p-3 bg-background rounded-lg border cursor-pointer hover:border-cyan-500/50 transition-colors">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center font-bold text-sm">
                                                                {entrant.number}
                                                            </div>
                                                            <div>
                                                                <p className="font-semibold">{entrant.name}</p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {entrant.jockey} / {entrant.trainer}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <Badge variant="outline">{entrant.odds_fractional}</Badge>
                                                            {entrant.timeform_stars && (
                                                                <Badge className="bg-yellow-500/20 text-yellow-600">
                                                                    {entrant.timeform_stars}★
                                                                </Badge>
                                                            )}
                                                            <ChevronDown className={`h-4 w-4 transition-transform ${expandedEntrants[entrant.name] ? 'rotate-180' : ''}`} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </CollapsibleTrigger>
                                            <CollapsibleContent>
                                                <div className="p-3 bg-muted/30 rounded-b-lg border border-t-0 space-y-3">
                                                    {/* Stats Grid */}
                                                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
                                                        <div className="p-2 bg-background rounded">
                                                            <p className="text-muted-foreground">OR</p>
                                                            <p className="font-semibold">{entrant.official_rating || 'N/A'}</p>
                                                        </div>
                                                        <div className="p-2 bg-background rounded">
                                                            <p className="text-muted-foreground">Weight</p>
                                                            <p className="font-semibold">{entrant.weight_lbs || 'N/A'} lbs</p>
                                                        </div>
                                                        <div className="p-2 bg-background rounded">
                                                            <p className="text-muted-foreground">Form</p>
                                                            <p className="font-semibold">{entrant.form || 'N/A'}</p>
                                                        </div>
                                                        <div className="p-2 bg-background rounded">
                                                            <p className="text-muted-foreground">Course Wins</p>
                                                            <p className="font-semibold">{entrant.career?.course_wins || '0'}</p>
                                                        </div>
                                                        <div className="p-2 bg-background rounded">
                                                            <p className="text-muted-foreground">Distance Wins</p>
                                                            <p className="font-semibold">{entrant.career?.distance_wins || '0'}</p>
                                                        </div>
                                                        <div className="p-2 bg-background rounded">
                                                            <p className="text-muted-foreground">Beaten Fav</p>
                                                            <p className="font-semibold">{entrant.career?.beaten_fav || '0'}</p>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Comments */}
                                                    {entrant.timeform_comment && (
                                                        <div className="p-2 bg-background rounded">
                                                            <p className="text-xs text-muted-foreground mb-1">Timeform Comment:</p>
                                                            <p className="text-sm">{entrant.timeform_comment}</p>
                                                        </div>
                                                    )}
                                                    {entrant.pinsticker_comment && (
                                                        <div className="p-2 bg-background rounded">
                                                            <p className="text-xs text-muted-foreground mb-1">Pinsticker Comment:</p>
                                                            <p className="text-sm">{entrant.pinsticker_comment}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </CollapsibleContent>
                                        </Collapsible>
                                    ))}
                                </div>
                            </ScrollArea>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
